/*
 * Project: bellcloud
 * File: apps/frontend/src/utils/logDownloadHandler.ts
 * Author: SWAN
 * Description: 로그 다운로드 전용 핸들러 (Class 16). 결정적 코덱 + 단순 루프.
 *   - 버스트: 큐에서 청크를 순차 수신 → 배치 업로드. 누락분만 single 재요청.
 *   - 노멀: 청크별 single 요청.
 *   방어적 제어(메트릭/적응형 타임아웃/읽기루프 재시작/버퍼 가드/백프레셔)는 제거.
 */

import type { GeneratePacketRequest } from '@packages/types/device/common/api.common';
import type { DeviceInfo } from '@packages/types/device/lite/deviceInfo';
import { logDownloadSessionManager } from './logDownloadSession';
import { FILE_TYPE_TO_REQUEST_FUNCTION_ID } from '@packages/types/device/lite/file_type_to_function_id';
import { MINI_FILE_TYPE_TO_REQUEST_FUNCTION_ID } from '@packages/types/device/mini/file_type_to_function_id';
import { SOF } from '@packages/protocols/constants';

export interface LogDownloadRequest {
  serial_number: string;
  file_type: number;
  days_of_gas_reading?: number;
  transfer_mode: number;
  connection_type: 'serial' | 'ble';
  log_id: string;
  chunk_index?: number; // 개별 청크 재요청 시 사용
}

export interface LogDownloadResult {
  success: boolean;
  log_id: string;
  file_type: number;
  num_packets: number;
  downloaded_packets: number;
  chunks_stored: boolean;
  version?: number;
  chunk_size?: number;
  total_size?: number;
  error_message?: string;
}

/** 어댑터 인터페이스 (Serial/BLE/시뮬 공통) */
interface LogDownloadAdapter {
  write(data: Uint8Array): Promise<void>;
  read(): Promise<Uint8Array>;
  readStreamPacket(timeoutMs?: number): Promise<Uint8Array>;
  setSinglePacketMode(): void;
  setStreamingMode(): void;
  clearBuffer(releaseReader?: boolean): void;
  stopBurstMode?(packet: Uint8Array): Promise<Uint8Array>;
}

/** 패킷 생성/파싱 API (현재는 모두 브라우저 로컬 코덱) */
interface PacketApiAdapter {
  generatePacket(dto: GeneratePacketRequest): Promise<{ packet: number[] }>;
  parsePacket(dto: {
    class_id: number;
    function_id: number;
    packet: number[];
    deviceInfo?: DeviceInfo;
  }): Promise<{ json: any }>;
}

type ChunkUploadCallback = (
  logId: string,
  serialNumber: string,
  chunkIndex: number,
  chunkData: Uint8Array
) => Promise<void> | void;

type ChunkBatchUploadCallback = (
  logId: string,
  serialNumber: string,
  chunks: Array<{ chunk_index: number; chunk_data: number[]; crc?: number }>
) => Promise<void>;

const delay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

export class LogDownloadHandler {
  private adapter: LogDownloadAdapter;
  private packetApi: PacketApiAdapter;
  private isMiniDevice: boolean;
  private chunkUploadCallback?: ChunkUploadCallback;
  private chunkBatchUploadCallback?: ChunkBatchUploadCallback;
  private isCancelled = false;

  private batchSize: number = 20;
  private pendingBatch: Array<{ chunk_index: number; chunk_data: number[]; crc?: number }> = [];
  private batchLogId: string = '';
  private batchSerialNumber: string = '';

  constructor(
    adapter: LogDownloadAdapter,
    packetApi: PacketApiAdapter,
    isMiniDevice: boolean = false,
    chunkUploadCallback?: ChunkUploadCallback,
    chunkBatchUploadCallback?: ChunkBatchUploadCallback,
    batchSize?: number
  ) {
    this.adapter = adapter;
    this.packetApi = packetApi;
    this.isMiniDevice = isMiniDevice;
    this.chunkUploadCallback = chunkUploadCallback;
    this.chunkBatchUploadCallback = chunkBatchUploadCallback;
    if (batchSize !== undefined && batchSize > 0) {
      this.batchSize = batchSize;
    }
  }

  /* ---------------- CANCEL ---------------- */
  async cancelDownload(): Promise<boolean> {
    this.isCancelled = true;
    try {
      const stopPacket = await this.packetApi.generatePacket({
        class_id: 16,
        function_id: 0x16, // stop sending log data in burst mode
        json_payload: {},
      });

      if (this.adapter.stopBurstMode) {
        const response = await this.adapter.stopBurstMode(new Uint8Array(stopPacket.packet));
        const parsed = await this.packetApi.parsePacket({
          class_id: 16,
          function_id: 0x17,
          packet: Array.from(response),
        });
        return (parsed.json?.Result || 0) === 1;
      }

      this.adapter.setSinglePacketMode();
      return true;
    } catch (error) {
      console.error('❌ [LogDownload] 다운로드 취소 실패:', error);
      this.adapter.setSinglePacketMode();
      return false;
    }
  }

  resetCancelFlag(): void {
    this.isCancelled = false;
  }

  /* ---------------- BATCH UPLOAD ---------------- */
  private async flushBatch(sessionId: string, fileType: number): Promise<void> {
    if (this.pendingBatch.length === 0) return;

    const batchToSend = [...this.pendingBatch];
    this.pendingBatch = [];

    try {
      if (this.chunkBatchUploadCallback) {
        await this.chunkBatchUploadCallback(this.batchLogId, this.batchSerialNumber, batchToSend);
        return;
      }
    } catch (error) {
      console.error(`❌ [LogDownload] 배치 업로드 실패, 세션에 보관:`, error);
    }
    // 콜백 없음/실패 시 세션 보관 (나중에 재전송)
    for (const chunk of batchToSend) {
      logDownloadSessionManager.addChunk(
        sessionId,
        fileType,
        chunk.chunk_index,
        Uint8Array.from(chunk.chunk_data)
      );
    }
  }

  private async addChunkToBatch(
    sessionId: string,
    fileType: number,
    chunkIndex: number,
    chunkData: Uint8Array,
    logId: string,
    serialNumber: string
  ): Promise<void> {
    if (this.pendingBatch.length === 0) {
      this.batchLogId = logId;
      this.batchSerialNumber = serialNumber;
    }
    this.pendingBatch.push({ chunk_index: chunkIndex, chunk_data: Array.from(chunkData) });
    if (this.pendingBatch.length >= this.batchSize) {
      await this.flushBatch(sessionId, fileType);
    }
  }

  /** 수신 청크를 배치/레거시 콜백/세션 중 하나로 보관 */
  private storeChunk(
    sessionId: string,
    fileType: number,
    chunkIndex: number,
    chunkData: Uint8Array,
    logId: string,
    serialNumber: string
  ): void {
    if (this.chunkBatchUploadCallback) {
      this.addChunkToBatch(sessionId, fileType, chunkIndex, chunkData, logId, serialNumber).catch(
        err => {
          console.error(`❌ [LogDownload] 배치 추가 실패 (청크 ${chunkIndex}):`, err);
          logDownloadSessionManager.addChunk(sessionId, fileType, chunkIndex, chunkData);
        }
      );
      return;
    }

    if (this.chunkUploadCallback) {
      try {
        const maybe = this.chunkUploadCallback(logId, serialNumber, chunkIndex, chunkData);
        if (maybe && typeof (maybe as Promise<void>).then === 'function') {
          (maybe as Promise<void>).catch(err => {
            console.error(`❌ [LogDownload] 청크 ${chunkIndex} 전송 실패, 세션 보관:`, err);
            logDownloadSessionManager.addChunk(sessionId, fileType, chunkIndex, chunkData);
          });
        }
      } catch (err) {
        console.error(`❌ [LogDownload] 청크 ${chunkIndex} 전송 실패, 세션 보관:`, err);
        logDownloadSessionManager.addChunk(sessionId, fileType, chunkIndex, chunkData);
      }
      return;
    }

    logDownloadSessionManager.addChunk(sessionId, fileType, chunkIndex, chunkData);
  }

  private async sendStopBurst(): Promise<void> {
    if (!this.adapter.stopBurstMode) return;
    try {
      const stopPacket = await this.packetApi.generatePacket({
        class_id: 16,
        function_id: 0x16,
        json_payload: {},
      });
      await this.adapter.stopBurstMode(new Uint8Array(stopPacket.packet));
    } catch (error) {
      console.error('❌ [LogDownload] 버스트 중단 실패:', error);
    }
  }

  /* ---------------- PACKET PARSING ---------------- */
  private getPayloadFromPacket(packet: Uint8Array): number[] {
    const sofIndex = packet.indexOf(SOF);
    if (sofIndex < 0 || sofIndex + 8 > packet.length) {
      throw new Error('유효하지 않은 패킷: SOF 없음 또는 헤더 길이 부족');
    }
    const lenL = packet[sofIndex + 6];
    const lenH = packet[sofIndex + 7];
    const payloadLen = lenL | (lenH << 8);
    const payloadStart = sofIndex + 8;
    const payloadEnd = payloadStart + payloadLen;
    if (payloadEnd > packet.length) {
      throw new Error(`유효하지 않은 패킷: payload 길이 초과 (${payloadEnd} > ${packet.length})`);
    }
    return Array.from(packet.slice(payloadStart, payloadEnd));
  }

  private readU16LE(payload: number[], offset: number): number {
    return payload[offset] | (payload[offset + 1] << 8);
  }

  private readU32LE(payload: number[], offset: number): number {
    return (
      (payload[offset] |
        (payload[offset + 1] << 8) |
        (payload[offset + 2] << 16) |
        (payload[offset + 3] << 24)) >>>
      0
    );
  }

  private parseHeaderPayload(payload: number[]): {
    file_type: number;
    version: number;
    chunk_size: number;
    num_packets: number;
    total_size: number;
  } {
    if (payload.length < 12) {
      throw new Error(`헤더 payload 길이 부족: ${payload.length}`);
    }
    return {
      file_type: this.readU16LE(payload, 0),
      version: this.readU16LE(payload, 2),
      chunk_size: this.readU16LE(payload, 4),
      num_packets: this.readU16LE(payload, 6),
      total_size: this.readU32LE(payload, 8),
    };
  }

  private parseChunkPayload(payload: number[]): { chunk_index: number; chunk_data: number[] } {
    if (payload.length < 2) {
      throw new Error(`청크 payload 길이 부족: ${payload.length}`);
    }
    return { chunk_index: this.readU16LE(payload, 0), chunk_data: payload.slice(2) };
  }

  /* ---------------- DOWNLOAD ---------------- */
  async downloadLog(
    request: LogDownloadRequest,
    sessionId: string,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<LogDownloadResult> {
    const { file_type, days_of_gas_reading, transfer_mode, log_id, chunk_index } = request;

    if (chunk_index !== undefined) {
      return this.downloadSingleChunk(request, sessionId);
    }

    try {
      this.resetCancelFlag();
      this.adapter.setSinglePacketMode();

      const isBurstMode = transfer_mode === 1;

      // 로그 요청 패킷 (Class 16, Function 1)
      const requestDto: GeneratePacketRequest = {
        class_id: 16,
        function_id: 1,
        json_payload: this.isMiniDevice
          ? { file_type, transfer_mode }
          : { file_type, days_of_gas_reading, transfer_mode },
      };
      const packetResponse = await this.packetApi.generatePacket(requestDto);

      // 버스트는 응답(0x02) 직후 청크가 바로 오므로 write 전에 스트리밍 모드 전환
      if (isBurstMode) {
        this.adapter.setStreamingMode();
      } else {
        this.adapter.clearBuffer(false);
      }

      await this.adapter.write(Uint8Array.from(packetResponse.packet));

      // 헤더 패킷 수신 (Class 16, Function 2)
      const headerPacketArray = isBurstMode
        ? await this.adapter.readStreamPacket(5000)
        : await this.adapter.read();

      const headerData = this.parseHeaderPayload(this.getPayloadFromPacket(headerPacketArray));
      const numPackets = headerData.num_packets;
      if (!numPackets || typeof numPackets !== 'number' || numPackets <= 0) {
        throw new Error(`유효하지 않은 num_packets: ${numPackets}`);
      }

      logDownloadSessionManager.initLogTypeInfo(sessionId, file_type, log_id, numPackets);

      if (!isBurstMode) {
        this.adapter.setStreamingMode();
      }

      let successCount = 0;
      const receivedIndices = new Set<number>();

      if (isBurstMode) {
        await this.runBurstLoop(request, sessionId, numPackets, receivedIndices, onProgress);
      } else {
        await this.runNormalLoop(request, sessionId, numPackets, receivedIndices, onProgress);
      }

      // 누락 청크는 single chunk로 재요청
      let finalMissing = this.computeMissing(numPackets, receivedIndices);
      if (isBurstMode && finalMissing.length > 0) {
        this.adapter.setSinglePacketMode();
        await delay(200); // readLoop이 모드 전환 반영하고 잔여 버스트 데이터 흡수할 시간
        this.adapter.clearBuffer(false);

        for (const idx of finalMissing) {
          if (this.isCancelled) break;
          try {
            const r = await this.downloadSingleChunk({ ...request, chunk_index: idx }, sessionId);
            if (r.success && r.chunks_stored) {
              receivedIndices.add(idx);
              if (onProgress) {
                onProgress(
                  (receivedIndices.size / numPackets) * 100,
                  receivedIndices.size,
                  numPackets
                );
              }
            }
          } catch (e) {
            console.warn(`⚠️ [LogDownload] single 재요청 실패 (청크 ${idx}):`, e);
          }
        }
        finalMissing = this.computeMissing(numPackets, receivedIndices);
      }
      successCount = receivedIndices.size;

      // 배치 잔여분 전송 + 세션에 남은 청크 업로드
      if (this.chunkBatchUploadCallback) {
        if (this.pendingBatch.length > 0) {
          await this.flushBatch(sessionId, file_type).catch(e =>
            console.error('❌ [LogDownload] 잔여 배치 전송 실패:', e)
          );
        }
        await this.uploadSessionChunks(sessionId, file_type, log_id, request.serial_number);
      }

      this.adapter.setSinglePacketMode();

      return {
        success: successCount === numPackets,
        log_id,
        file_type,
        num_packets: numPackets,
        downloaded_packets: successCount,
        chunks_stored: true,
        version: headerData.version,
        chunk_size: headerData.chunk_size,
        total_size: headerData.total_size,
        error_message:
          successCount < numPackets ? `${numPackets - successCount}개 청크 누락` : undefined,
      };
    } catch (error) {
      this.adapter.setSinglePacketMode();
      console.error('❌ [LogDownload] 로그 다운로드 실패:', error);
      return {
        success: false,
        log_id,
        file_type,
        num_packets: 0,
        downloaded_packets: 0,
        chunks_stored: false,
        error_message: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /** 버스트 수신 루프: 큐에서 청크를 순차로 받아 dedup/검증/보관 */
  private async runBurstLoop(
    request: LogDownloadRequest,
    sessionId: string,
    numPackets: number,
    receivedIndices: Set<number>,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<void> {
    const { file_type, log_id } = request;
    const startTime = Date.now();
    const totalTimeout = Math.max(180000, Math.min(600000, numPackets * 1000 + 60000));
    const chunkTimeout = numPackets > 500 ? 20000 : 15000;
    const MAX_CONSECUTIVE_FAILURES = 5;
    const maxReads = numPackets + 500;

    const expectedFunctionIds = this.isMiniDevice
      ? [0x04, 0x18]
      : [0x04, 0x06, 0x08, 0x0a, 0x0c, 0x0e, 0x18, 0x19, 0x1a, 0x1b, 0x1c, 0x1d];

    let consecutiveFailures = 0;
    let readCount = 0;

    while (receivedIndices.size < numPackets && readCount < maxReads) {
      readCount++;

      if (this.isCancelled) throw new Error('다운로드가 사용자에 의해 취소되었습니다');
      if (Date.now() - startTime > totalTimeout) {
        console.warn(
          `⚠️ [LogDownload] 버스트 전체 타임아웃, 수신 ${receivedIndices.size}/${numPackets}`
        );
        break;
      }
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        await this.sendStopBurst();
        break;
      }

      try {
        const isLast = receivedIndices.size >= numPackets - 1;
        const timeout = isLast ? Math.max(30000, chunkTimeout * 2) : chunkTimeout;
        const packetData = await this.adapter.readStreamPacket(timeout);

        const functionId = packetData[5];
        if (!expectedFunctionIds.includes(functionId)) continue;

        const { chunk_index: chunkIndex, chunk_data } = this.parseChunkPayload(
          this.getPayloadFromPacket(packetData)
        );
        if (chunkIndex < 0 || chunkIndex >= numPackets) continue;
        if (receivedIndices.has(chunkIndex)) continue;

        receivedIndices.add(chunkIndex);
        consecutiveFailures = 0;

        if (onProgress) {
          onProgress((receivedIndices.size / numPackets) * 100, receivedIndices.size, numPackets);
        }
        this.storeChunk(
          sessionId,
          file_type,
          chunkIndex,
          Uint8Array.from(chunk_data),
          log_id,
          request.serial_number
        );
      } catch (readError) {
        if (readError instanceof Error && readError.message.includes('Timeout')) {
          consecutiveFailures++;
        }
        await delay(150);
      }
    }
  }

  /** 노멀 수신 루프: 청크별 single 요청 */
  private async runNormalLoop(
    request: LogDownloadRequest,
    sessionId: string,
    numPackets: number,
    receivedIndices: Set<number>,
    onProgress?: (progress: number, current: number, total: number) => void
  ): Promise<void> {
    for (let i = 0; i < numPackets; i++) {
      if (this.isCancelled) throw new Error('다운로드가 사용자에 의해 취소되었습니다');
      try {
        const r = await this.downloadSingleChunk({ ...request, chunk_index: i }, sessionId);
        if (r.success && r.chunks_stored) {
          receivedIndices.add(i);
          if (onProgress) onProgress(((i + 1) / numPackets) * 100, i + 1, numPackets);
        }
      } catch (e) {
        console.error(`❌ [LogDownload] 노멀 모드 청크 ${i} 수신 실패:`, e);
      }
    }
  }

  private computeMissing(numPackets: number, receivedIndices: Set<number>): number[] {
    const missing: number[] = [];
    for (let i = 0; i < numPackets; i++) {
      if (!receivedIndices.has(i)) missing.push(i);
    }
    return missing;
  }

  /** 세션에 보관된 청크를 배치(동시성 5, 실패 시 재시도 3회)로 업로드 */
  private async uploadSessionChunks(
    sessionId: string,
    fileType: number,
    logId: string,
    serialNumber: string
  ): Promise<void> {
    if (!this.chunkBatchUploadCallback) return;

    const sessionChunks = logDownloadSessionManager.getChunks(sessionId, fileType);
    if (sessionChunks.length === 0) return;

    const batches: Array<{ chunk_index: number; chunk_data: number[] }>[] = [];
    for (let i = 0; i < sessionChunks.length; i += this.batchSize) {
      batches.push(
        sessionChunks.slice(i, i + this.batchSize).map(c => ({
          chunk_index: c.chunk_index,
          chunk_data: c.chunk_data,
        }))
      );
    }

    const concurrency = 5;
    let uploadedCount = 0;
    const failed: Array<(typeof batches)[number]> = [];

    for (let i = 0; i < batches.length; i += concurrency) {
      const group = batches.slice(i, i + concurrency);
      await Promise.all(
        group.map(async batchData => {
          try {
            await this.chunkBatchUploadCallback!(logId, serialNumber, batchData);
            uploadedCount += batchData.length;
          } catch (e) {
            console.error('❌ [LogDownload] 배치 업로드 실패:', e);
            failed.push(batchData);
          }
        })
      );
    }

    for (const batchData of failed) {
      let ok = false;
      for (let retry = 0; retry < 3 && !ok; retry++) {
        try {
          await delay(1000 * (retry + 1));
          await this.chunkBatchUploadCallback!(logId, serialNumber, batchData);
          uploadedCount += batchData.length;
          ok = true;
        } catch (e) {
          console.warn(`⚠️ [LogDownload] 배치 재시도 ${retry + 1}/3 실패:`, e);
        }
      }
    }

    if (uploadedCount === sessionChunks.length) {
      logDownloadSessionManager.clearChunks(sessionId, fileType);
    } else {
      console.warn(
        `⚠️ [LogDownload] 일부 청크 업로드 실패 (${uploadedCount}/${sessionChunks.length}), 세션 유지`
      );
    }
  }

  /** 개별 청크 다운로드 (노멀 모드 / 누락 재요청용) */
  private async downloadSingleChunk(
    request: LogDownloadRequest,
    sessionId: string
  ): Promise<LogDownloadResult> {
    const { file_type, chunk_index, log_id } = request;
    if (chunk_index === undefined) {
      throw new Error('chunk_index is required for single chunk download');
    }

    try {
      this.adapter.setSinglePacketMode();

      const functionIdMap = this.isMiniDevice
        ? MINI_FILE_TYPE_TO_REQUEST_FUNCTION_ID
        : FILE_TYPE_TO_REQUEST_FUNCTION_ID;
      const functionId = functionIdMap[file_type as keyof typeof functionIdMap] || 0;

      let chunkIndexParamName = 'chunk_index';
      if (this.isMiniDevice) {
        chunkIndexParamName = functionId === 0x03 ? 'datalog_chunk_index' : 'eventlog_chunk_index';
      }

      const requestDto: GeneratePacketRequest = {
        class_id: 16,
        function_id: functionId,
        json_payload: { [chunkIndexParamName]: chunk_index },
      };
      const packetResponse = await this.packetApi.generatePacket(requestDto);

      this.adapter.clearBuffer(false);
      await this.adapter.write(Uint8Array.from(packetResponse.packet));

      const chunkPacketArray = await this.adapter.read();
      const { chunk_index: actualChunkIndex, chunk_data } = this.parseChunkPayload(
        this.getPayloadFromPacket(chunkPacketArray)
      );
      logDownloadSessionManager.addChunk(
        sessionId,
        file_type,
        actualChunkIndex,
        Uint8Array.from(chunk_data)
      );

      return {
        success: true,
        log_id,
        file_type,
        num_packets: 1,
        downloaded_packets: 1,
        chunks_stored: true,
      };
    } catch (error) {
      console.error(`❌ [LogDownload] 청크 ${chunk_index} 다운로드 실패:`, error);
      return {
        success: false,
        log_id,
        file_type,
        num_packets: 1,
        downloaded_packets: 0,
        chunks_stored: false,
        error_message: error instanceof Error ? error.message : String(error),
      };
    }
  }
}
